import React, { useState, useEffect, useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../../context/SessionContext";
import { getFirestore, collection, doc, getDocs, getDoc } from "firebase/firestore";
import app from "../../firebase";
import ResetPassword from "../admin/users/ResetPassword";
import { toCalendarDateKey } from "../../utils/announcementDates";

const LineSVG = ({ width = 700, height = 280, data = [] }) => {
  if (!data || data.length === 0) return <div style={{ height }} />;
  const padding = 24;
  const w = width - padding * 2;
  const h = height - padding * 2;
  const max = Math.max(...data.map(d => d.applicants || 0), 1);
  const stepX = w / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = padding + i * stepX;
    const y = padding + (1 - (d.applicants || 0) / max) * h;
    return { x, y, label: d.month, value: d.applicants || 0 };
  });
  const pathD = points.map((p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `L ${p.x} ${p.y}`)).join(' ');
  return (
    <svg width={width} height={height} role="img" aria-label="Applicant trend">
      <rect x={0} y={0} width={width} height={height} rx={8} fill="transparent" />
      <g>
        {[0, 0.25, 0.5, 0.75, 1].map((t, idx) => (
          <line key={idx} x1={padding} x2={width - padding} y1={padding + t * h} y2={padding + t * h} stroke="#f0f0f0" strokeWidth={1} />
        ))}
        <path d={`${pathD} L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`} fill="rgba(21,92,43,0.08)" stroke="none" />
        <path d={pathD} fill="none" stroke="#155c2b" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={3.5} fill="#155c2b" />
        ))}
      </g>
      <g>
        {points.map((p, i) => (
          <text key={i} x={p.x} y={height - 6} fontSize={11} fill="#667085" textAnchor="middle">{p.label}</text>
        ))}
      </g>
    </svg>
  );
};

const PieSVG = ({ width = 380, height = 280, data = [], colors = ['#e67e22', '#1e8f4a', '#155c2b'] }) => {
  const cx = width / 2;
  const cy = height / 2 - 10;
  const radius = Math.min(width, height) / 2 - 20;
  const total = data.reduce((s, d) => s + (d.value || 0), 0) || 1;
  let startAngle = -90;
  const arcs = data.map((d) => {
    const value = d.value || 0;
    const angle = (value / total) * 360;
    const endAngle = startAngle + angle;
    const large = angle > 180 ? 1 : 0;
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const dPath = [`M ${cx} ${cy}`, `L ${start.x} ${start.y}`, `A ${radius} ${radius} 0 ${large} 0 ${end.x} ${end.y}`, 'Z'].join(' ');
    startAngle = endAngle;
    return { dPath, label: d.name, value: value };
  });
  return (
    <svg width={width} height={height} role="img" aria-label="Application status share">
      <g>
        {arcs.map((a, i) => (
          <path key={i} d={a.dPath} fill={colors[i % colors.length]} stroke="#fff" strokeWidth={1} />
        ))}
      </g>
      <g>
        {data.map((d, i) => (
          <g key={i} transform={`translate(${20}, ${height - 60 + i * 18})`}>
            <rect width={12} height={12} fill={colors[i % colors.length]} />
            <text x={18} y={10} fontSize={12} fill="#333">{d.name} ({d.value})</text>
          </g>
        ))}
      </g>
    </svg>
  );
};

function polarToCartesian(cx, cy, r, angleDeg) {
  const a = ((angleDeg - 90) * Math.PI) / 180.0;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

const BarsSVG = ({ width = 700, height = 280, data = [], color = '#155c2b' }) => {
  if (!data || data.length === 0) return <div style={{ height }} />;
  const padding = 24;
  const w = width - padding * 2;
  const h = height - padding * 2;
  const max = Math.max(...data.map(d => d.value || 0), 1);
  const barWidth = Math.max(20, w / Math.max(1, data.length) * 0.5);
  return (
    <svg width={width} height={height} role="img" aria-label="Bar chart">
      <rect x={0} y={0} width={width} height={height} rx={8} fill="transparent" />
      <g>
        {[0, 0.25, 0.5, 0.75, 1].map((t, idx) => (
          <line key={idx} x1={padding} x2={width - padding} y1={padding + t * h} y2={padding + t * h} stroke="#f0f0f0" strokeWidth={1} />
        ))}
        {data.map((d, i) => {
          const x = padding + i * (w / data.length) + ((w / data.length) - barWidth) / 2;
          const barHeight = ((d.value || 0) / max) * h;
          return (
            <g key={i}>
              <rect x={x} y={padding + h - barHeight} width={barWidth} height={barHeight} fill={color} rx={4} />
              <text x={x + barWidth / 2} y={height - 6} fontSize={11} fill="#667085" textAnchor="middle">{d.label}</text>
            </g>
          );
        })}
      </g>
    </svg>
  );
};

const StatCard = ({ title, value, subtitle, color = "#0f5f3b", icon }) => (
  <div style={{
    background: "#fff",
    borderRadius: 12,
    padding: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    border: `3px solid ${color}`,
    position: "relative",
    overflow: "hidden"
  }}>
    <div style={{
      position: "absolute",
      top: -20,
      right: -20,
      fontSize: 80,
      opacity: 0.1,
      color: color
    }}>
      {icon}
    </div>
    <div style={{ fontSize: 14, color: "#666", marginBottom: 8, fontWeight: 600 }}>{title}</div>
    <div style={{ fontSize: 36, fontWeight: 700, color: color, marginBottom: 4 }}>{value}</div>
    {subtitle && <div style={{ fontSize: 12, color: "#888" }}>{subtitle}</div>}
  </div>
);

const BreakdownCard = ({ title, data, color = "#0f5f3b" }) => (
  <div style={{
    background: "#fff",
    borderRadius: 12,
    padding: "24px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.08)"
  }}>
    <div style={{ fontSize: 18, fontWeight: 700, color: "#333", marginBottom: 16 }}>{title}</div>
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {Object.entries(data).map(([key, value]) => {
        const total = Object.values(data).reduce((sum, v) => sum + v, 0);
        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
        return (
          <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 14, color: "#555", fontWeight: 600 }}>{key}</span>
              <span style={{ fontSize: 14, color: "#333", fontWeight: 700 }}>{value}</span>
            </div>
            <div style={{ width: "100%", height: 8, background: "#e0e0e0", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${percentage}%`, height: "100%", background: color, transition: "width 0.3s ease" }} />
            </div>
            <div style={{ fontSize: 11, color: "#888" }}>{percentage}%</div>
          </div>
        );
      })}
    </div>
  </div>
);

function parseStudentDate(val) {
  if (!val) return null;
  if (typeof val === "string") return new Date(val);
  if (typeof val?.toDate === "function") return val.toDate();
  if (val instanceof Date) return val;
  return null;
}

const navLinkStyle = (active) => ({
  background: active ? "#e67e22" : "transparent",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "14px 32px",
  fontWeight: 600,
  fontSize: 16,
  cursor: "pointer",
  textAlign: "left",
  marginBottom: 8,
  marginRight: 0,
  textDecoration: "none",
  display: "block",
  transition: "background 0.2s, color 0.2s",
  boxShadow: active ? "0 2px 8px #e67e22aa" : "none"
});

export default function RegistarDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, currentUser } = useSession();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [registrarEmail, setRegistrarEmail] = useState(currentUser?.email || "");
  const [showResetModal, setShowResetModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState("all");
  const [selectedSemester, setSelectedSemester] = useState("all");
  const [announcement, setAnnouncement] = useState(null);
  const [schoolYears, setSchoolYears] = useState([]);
  const [analytics, setAnalytics] = useState({
    totalStudents: 0,
    currentApplicantsReview: 0,
    approvedApplicants: 0,
    enrolledCount: 0,
    passCount: 0,
    failCount: 0,
    byStatus: {},
    byProgram: {},
    byYearLevel: {},
    requirementsStats: {
      cleared: 0,
      pending: 0,
    }
  });
  const profilePicUrl = "https://ui-avatars.com/api/?name=Registrar";

  useEffect(() => {
    if (currentUser === null) {
      navigate("/registar-login");
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    try { setRegistrarEmail(currentUser?.email || ""); } catch (e) {}
  }, [currentUser]);

  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const db = getFirestore(app);
        const announcementRef = doc(db, "systemSettings", "announcement");
        const snap = await getDoc(announcementRef);
        if (snap.exists()) {
          const data = snap.data();
          setAnnouncement(data);
          const years = [];
          if (data.firstSemYearStart && data.firstSemYearEnd) {
            years.push({
              key: `first-${data.firstSemYearStart}-${data.firstSemYearEnd}`,
              label: `${data.firstSemYearStart}-${data.firstSemYearEnd}`,
              sem: "first",
              start: data.firstSemStart,
              end: data.firstSemEnd,
            });
          }
          if (data.secondSemYearStart && data.secondSemYearEnd) {
            years.push({
              key: `second-${data.secondSemYearStart}-${data.secondSemYearEnd}`,
              label: `${data.secondSemYearStart}-${data.secondSemYearEnd}`,
              sem: "second",
              start: data.secondSemStart,
              end: data.secondSemEnd,
            });
          }
          setSchoolYears(years);
        }
      } catch (err) {
        console.error("Error loading announcement settings:", err);
      }
    };

    fetchAnnouncement();
  }, []);

  useEffect(() => {
    const fetchStudents = async () => {
      setLoading(true);
      try {
        const db = getFirestore(app);
        const studentsRef = collection(db, "students");
        const snap = await getDocs(studentsRef);
        const studentsData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setStudents(studentsData);

        const stats = {
          totalStudents: studentsData.length,
          currentApplicantsReview: 0,
          approvedApplicants: 0,
          enrolledCount: 0,
          passCount: 0,
          failCount: 0,
          byStatus: {},
          byProgram: {},
          byYearLevel: {},
          requirementsStats: {
            cleared: 0,
            pending: 0,
          }
        };

        studentsData.forEach(student => {
          const status = student.registrationStatus || student.requirementsStatus || student.appointment?.status || "Pending";
          stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;

        if (student.program) {
          stats.byProgram[student.program] = (stats.byProgram[student.program] || 0) + 1;
        }

        const yearLevel = student.yearLevel || student.preRegistration?.entryLevel;
        if (yearLevel) {
          stats.byYearLevel[yearLevel] = (stats.byYearLevel[yearLevel] || 0) + 1;
        }

          if (!student.idNumber) {
            stats.currentApplicantsReview += 1;
          } else {
            stats.approvedApplicants += 1;
          }

          if (student.registrationStatus === "enrolled") {
            stats.enrolledCount += 1;
          }

          if (student.appointment?.status === "approved") {
            stats.passCount += 1;
          }
          if (student.appointment?.status === "disapproved") {
            stats.failCount += 1;
          }

            const requirementsStatus = student.requirementsStatus ||
            (student.requirements && Object.values(student.requirements).length > 0
              ? (Object.values(student.requirements).every(Boolean) ? "Submitted" : "Pending")
              : null);

          if (requirementsStatus === "Submitted") {
            stats.requirementsStats.cleared += 1;
          } else {
            stats.requirementsStats.pending += 1;
          }
        });

        setAnalytics(stats);
      } catch (err) {
        console.error("Error loading registar analytics:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, []);

  const uniqueSchoolYears = useMemo(() => {
    const map = new Map();
    schoolYears.forEach(s => {
      if (!map.has(s.label)) map.set(s.label, s);
    });
    return Array.from(map.values());
  }, [schoolYears]);

  const schoolYearOptions = useMemo(() => [...uniqueSchoolYears.map(y => y.label)], [uniqueSchoolYears]);

  const semesterOptions = useMemo(() => [
    { value: "first", label: "First Semester" },
    { value: "second", label: "Second Semester" },
  ], []);

  const selectedRange = useMemo(() => {
    if (selectedSchoolYear === "all" || !announcement) return null;
    const fy1 = announcement.firstSemYearStart && announcement.firstSemYearEnd
      ? `${announcement.firstSemYearStart}-${announcement.firstSemYearEnd}`
      : null;
    const fy2 = announcement.secondSemYearStart && announcement.secondSemYearEnd
      ? `${announcement.secondSemYearStart}-${announcement.secondSemYearEnd}`
      : null;

    if (selectedSemester === "first") {
      if (fy1 === selectedSchoolYear) return { start: announcement.firstSemStart, end: announcement.firstSemEnd };
      if (fy2 === selectedSchoolYear) return { start: announcement.secondSemStart, end: announcement.secondSemEnd };
      return null;
    }

    if (selectedSemester === "second") {
      if (fy2 === selectedSchoolYear) return { start: announcement.secondSemStart, end: announcement.secondSemEnd };
      if (fy1 === selectedSchoolYear) return { start: announcement.firstSemStart, end: announcement.firstSemEnd };
      return null;
    }

    if (fy1 === selectedSchoolYear) return { start: announcement.firstSemStart, end: announcement.firstSemEnd };
    if (fy2 === selectedSchoolYear) return { start: announcement.secondSemStart, end: announcement.secondSemEnd };
    return null;
  }, [selectedSchoolYear, selectedSemester, announcement]);

  const filteredStudents = useMemo(() => {
    if (!selectedRange) return students;
    return students.filter(student => {
      const d = parseStudentDate(student.createdAt);
      if (!d) return false;
      const studentDateKey = toCalendarDateKey(d);
      const startKey = toCalendarDateKey(selectedRange.start);
      const endKey = toCalendarDateKey(selectedRange.end);
      return (!startKey || studentDateKey >= startKey) && (!endKey || studentDateKey <= endKey);
    });
  }, [students, selectedRange]);

  const filteredAnalytics = useMemo(() => {
    const stats = {
      totalStudents: filteredStudents.length,
      currentApplicantsReview: 0,
      approvedApplicants: 0,
      enrolledCount: 0,
      passCount: 0,
      failCount: 0,
      byStatus: {},
      byProgram: {},
      byYearLevel: {},
      requirementsStats: {
        cleared: 0,
        pending: 0,
      }
    };

    filteredStudents.forEach(student => {
      const status = student.registrationStatus || student.requirementsStatus || student.appointment?.status || "Pending";
      stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;

      if (student.program) {
        stats.byProgram[student.program] = (stats.byProgram[student.program] || 0) + 1;
      }

      const yearLevel = student.yearLevel || student.preRegistration?.entryLevel;
      if (yearLevel) {
        stats.byYearLevel[yearLevel] = (stats.byYearLevel[yearLevel] || 0) + 1;
      }

      if (!student.idNumber) {
        stats.currentApplicantsReview += 1;
      } else {
        stats.approvedApplicants += 1;
      }

      if (student.registrationStatus === "enrolled") {
        stats.enrolledCount += 1;
      }

      if (student.appointment?.status === "approved") {
        stats.passCount += 1;
      }
      if (student.appointment?.status === "disapproved") {
        stats.failCount += 1;
      }

      const requirementsStatus = student.requirementsStatus ||
        (student.requirements && Object.values(student.requirements).length > 0
          ? (Object.values(student.requirements).every(Boolean) ? "Submitted" : "Pending")
          : null);

      if (requirementsStatus === "Submitted") {
        stats.requirementsStats.cleared += 1;
      } else {
        stats.requirementsStats.pending += 1;
      }
    });

    return stats;
  }, [filteredStudents]);

  const trendLabels = useMemo(() => {
    const labels = [];
    const now = new Date();
    const current = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    for (let i = 0; i < 6; i += 1) {
      labels.push(`${current.toLocaleString('default', { month: 'short' })} ${current.getFullYear()}`);
      current.setMonth(current.getMonth() + 1);
    }
    return labels;
  }, []);

  const trendData = useMemo(() => {
    const counts = trendLabels.map(() => 0);
    filteredStudents.forEach(student => {
      const d = parseStudentDate(student.createdAt);
      if (!d) return;
      const label = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
      const idx = trendLabels.indexOf(label);
      if (idx >= 0) counts[idx] += 1;
    });
    return trendLabels.map((label, index) => ({ month: label, applicants: counts[index] }));
  }, [filteredStudents, trendLabels]);

  const enrolledTrendData = useMemo(() => {
    const counts = trendLabels.map(() => 0);
    filteredStudents.forEach(student => {
      if (student.registrationStatus !== 'enrolled') return;
      const d = parseStudentDate(student.enrolledAt || student.createdAt);
      if (!d) return;
      const label = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
      const idx = trendLabels.indexOf(label);
      if (idx >= 0) counts[idx] += 1;
    });
    return trendLabels.map((label, index) => ({ label, value: counts[index] }));
  }, [filteredStudents, trendLabels]);

  const programChartData = useMemo(() => {
    return Object.entries(filteredAnalytics.byProgram)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ label: name, value }));
  }, [filteredAnalytics.byProgram]);

  const yearLevelChartData = useMemo(() => {
    return Object.entries(filteredAnalytics.byYearLevel)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ label: name, value }));
  }, [filteredAnalytics.byYearLevel]);

  const statusData = useMemo(() => ([
    { name: 'Review', value: filteredAnalytics.currentApplicantsReview },
    { name: 'Approved', value: filteredAnalytics.approvedApplicants },
    { name: 'Enrolled', value: filteredAnalytics.enrolledCount },
  ]), [filteredAnalytics]);

  const showAnalytics = ["/registar/dashboard", "/registar/dashboard/analytics"].includes(location.pathname.replace(/\/$/, ""));

  const handleLogout = async () => {
    try {
      await logout();
      localStorage.removeItem('registrarPortalLogin');
      localStorage.removeItem('registrarPortalRole');
      navigate("/registar-login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  return (
    <div style={{ height: "100vh", overflow: "hidden", background: "#f5f5f5", display: "flex", flexDirection: "row" }}>
      <div style={{ width: 240, background: "#155c2b", color: "#fff", display: "flex", flexDirection: "column", alignItems: "stretch", padding: "32px 0", position: "fixed", top: 0, left: 0, bottom: 0, overflow: "hidden", boxShadow: "2px 0 8px #eee" }}>
        <div style={{ fontWeight: 700, fontSize: 24, textAlign: "center", marginBottom: 40, letterSpacing: 2 }}>ENROLLEASE</div>

        <Link to="/registar/dashboard" style={navLinkStyle(location.pathname === "/registar/dashboard")}>Home</Link>
        <Link to="/registar/dashboard/review-enrollment" style={navLinkStyle(location.pathname === "/registar/dashboard/review-enrollment")}>Review Enrollment</Link>
        <Link to="/registar/dashboard/miscellaneous-fees" style={navLinkStyle(location.pathname === "/registar/dashboard/miscellaneous-fees")}>Miscellaneous Fees</Link>
        <Link to="/registar/dashboard/students-requirements" style={navLinkStyle(location.pathname === "/registar/dashboard/students-requirements")}>Students Requirements</Link>
        <Link to="/registar/dashboard/curriculum/register" style={navLinkStyle(location.pathname === "/registar/dashboard/curriculum/register")}>Register Curriculum</Link>
        <Link to="/registar/dashboard/announcement" style={navLinkStyle(location.pathname === "/registar/dashboard/announcement")}>System Announcement</Link>

        <div style={{ flex: 1 }} />
      </div>

      <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100vh", marginLeft: 240, overflowY: "auto" }}>
        <div style={{ height: 80, background: "#fff", display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", padding: "0 40px", position: "fixed", top: 0, right: 0, left: 240, zIndex: 10, justifyContent: "flex-end" }}>
          <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
            <span style={{ marginRight: 16, fontWeight: 600, fontSize: 18, color: "#155c2b" }}>Registrar</span>
            <button onClick={() => setDropdownOpen(!dropdownOpen)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}>
              <img src={profilePicUrl} alt="Profile" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", border: "2px solid #e67e22" }} />
            </button>
            {dropdownOpen && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, background: "#fff", border: "1px solid #e0e0e0", borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.1)", zIndex: 20, minWidth: 200, overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", fontWeight: 600, color: "#155c2b", borderBottom: "1px solid #f0f0f0" }}>Registrar</div>
                <button onClick={() => { setDropdownOpen(false); setShowResetModal(true); }} style={{ width: "100%", padding: "12px 16px", background: "transparent", border: "none", color: "#155c2b", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "background 0.2s" }} onMouseEnter={(e) => e.target.style.background = "#f5fff0"} onMouseLeave={(e) => e.target.style.background = "transparent"}>Change Password</button>
                <button onClick={() => { setDropdownOpen(false); handleLogout(); }} style={{ width: "100%", padding: "12px 16px", background: "transparent", border: "none", color: "#e67e22", fontSize: 14, fontWeight: 600, cursor: "pointer", textAlign: "left", transition: "background 0.2s" }} onMouseEnter={(e) => e.target.style.background = "#fef5f0"} onMouseLeave={(e) => e.target.style.background = "transparent"}>Log out</button>
                {showResetModal && <ResetPassword email={registrarEmail} onClose={() => setShowResetModal(false)} />}
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: 1, padding: "120px 40px 40px", display: "flex", flexDirection: "column", minHeight: 0, boxSizing: "border-box" }}>
          {showAnalytics ? (
            <>
              {loading ? (
                <div style={{ textAlign: "center", padding: 48, color: "#666" }}>Loading analytics data...</div>
              ) : (
                <>
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 28, fontWeight: 700, color: "#0f5f3b", marginBottom: 8 }}>Registrar Analytics</div>
                    <div style={{ fontSize: 14, color: "#666" }}>Overview of enrollment and entrance exam performance.</div>
                  </div>

                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center', marginBottom: 24 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#4b5563' }}>School Year</label>
                      <select value={selectedSchoolYear} onChange={e => setSelectedSchoolYear(e.target.value)} style={{ padding: 12, borderRadius: 8, border: '1px solid #d1d5db', minWidth: 180 }}>
                        <option value="all">All School Years</option>
                        {schoolYearOptions.map(year => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <label style={{ fontSize: 13, color: '#4b5563' }}>Semester</label>
                      <select value={selectedSemester} onChange={e => setSelectedSemester(e.target.value)} style={{ padding: 12, borderRadius: 8, border: '1px solid #d1d5db', minWidth: 180 }}>
                        <option value="all">All Semesters</option>
                        {semesterOptions.map(sem => (
                          <option key={sem.value} value={sem.value}>{sem.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20, marginBottom: 32 }}>
                    <StatCard title="Current Applicants (Review)" value={filteredAnalytics.currentApplicantsReview} subtitle="Students awaiting review" color="#0f5f3b" icon="👀" />
                    <StatCard title="Approved Applicants" value={filteredAnalytics.approvedApplicants} subtitle="Applications approved" color="#2e7d32" icon="✅" />
                    <StatCard title="Total Applicants" value={filteredAnalytics.totalStudents} subtitle="All student applications" color="#155c2b" icon="📊" />
                    <StatCard title="Total Enrolled" value={filteredAnalytics.enrolledCount} subtitle="Officially enrolled" color="#1e88e5" icon="🎓" />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 0.8fr', gap: 20, marginBottom: 32 }}>
                    <div style={{ background: '#fff', padding: 20, borderRadius: 12, border: '1px solid #eee' }}>
                      <div style={{ fontWeight: 700, marginBottom: 16 }}>Applicant Trend</div>
                      <div style={{ width: '100%', overflowX: 'auto' }}>
                        <LineSVG width={700} height={280} data={trendData} />
                      </div>
                    </div>
                    <div style={{ background: '#fff', padding: 20, borderRadius: 12, border: '1px solid #eee' }}>
                      <div style={{ fontWeight: 700, marginBottom: 16 }}>Application Status Share</div>
                      <PieSVG width={380} height={280} data={statusData} colors={['#e67e22', '#1e8f4a', '#155c2b']} />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 20, marginBottom: 32 }}>
                    <div style={{ background: '#fff', padding: 20, borderRadius: 12, border: '1px solid #eee' }}>
                      <div style={{ fontWeight: 700, marginBottom: 16 }}>Total Enrolled Trend</div>
                      <BarsSVG width={700} height={280} data={enrolledTrendData} color="#1e88e5" />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 20, marginBottom: 32 }}>
                    <div style={{ background: '#fff', padding: 20, borderRadius: 12, border: '1px solid #eee' }}>
                      <div style={{ fontWeight: 700, marginBottom: 16 }}>By Program/Department</div>
                      <BarsSVG width={700} height={280} data={programChartData} color="#1976d2" />
                    </div>
                    <div style={{ background: '#fff', padding: 20, borderRadius: 12, border: '1px solid #eee' }}>
                      <div style={{ fontWeight: 700, marginBottom: 16 }}>By Year Level</div>
                      <BarsSVG width={700} height={280} data={yearLevelChartData} color="#e67e22" />
                    </div>
                  </div>

                  <div style={{ marginBottom: 32 }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f5f3b', marginBottom: 24 }}>Enrollment Statistics</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 24 }}>
                      <BreakdownCard title="By Registration Status" data={filteredAnalytics.byStatus} color="#0f5f3b" />
                      <BreakdownCard title="By Program/Department" data={filteredAnalytics.byProgram} color="#1976d2" />
                      <BreakdownCard title="By Year Level" data={filteredAnalytics.byYearLevel} color="#e67e22" />
                    </div>
                  </div>

                  <div style={{ background: '#fff', borderRadius: 12, padding: 32, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: '#0f5f3b', marginBottom: 24 }}>Requirements & Clearance Status</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
                      <div style={{ padding: 24, background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)', borderRadius: 12, color: '#fff', boxShadow: '0 4px 12px rgba(46,125,50,0.3)' }}>
                        <div style={{ fontSize: 14, marginBottom: 8, opacity: 0.9 }}>Cleared Students</div>
                        <div style={{ fontSize: 36, fontWeight: 700 }}>{filteredAnalytics.requirementsStats.cleared}</div>
                        <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>{filteredAnalytics.totalStudents ? ((filteredAnalytics.requirementsStats.cleared / filteredAnalytics.totalStudents) * 100).toFixed(1) : 0}% of total</div>
                      </div>
                      <div style={{ padding: 24, background: 'linear-gradient(135deg, #f57c00 0%, #fb8c00 100%)', borderRadius: 12, color: '#fff', boxShadow: '0 4px 12px rgba(245,124,0,0.3)' }}>
                        <div style={{ fontSize: 14, marginBottom: 8, opacity: 0.9 }}>Pending Requirements</div>
                        <div style={{ fontSize: 36, fontWeight: 700 }}>{filteredAnalytics.requirementsStats.pending}</div>
                        <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>{filteredAnalytics.totalStudents ? ((filteredAnalytics.requirementsStats.pending / filteredAnalytics.totalStudents) * 100).toFixed(1) : 0}% of total</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </div>
  );
}
